import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import React, { useEffect } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { BrowserRouter, Route, Routes, useNavigate, useSearchParams } from 'react-router-dom';

import { AppContextProvider, Env } from '../../contexts/AppContext';
import { useScrollToTop } from '../../hooks';
import useCurrentUser from '../../hooks/useCurrentUser';
import { STRIPE_PUBLISH_KEY } from '../../src/env';
import { User } from '../../utils/backend-api/currentUser';
import { EMAIL_MESSAGE_CONFIRMATION } from '../../utils/constants';
import { CustomToaster } from '../BaseUI';
import CommunityInsightsPage from '../CommunityInsightsPage';
import EditForumPostPage from '../Forum/EditForumPostPage';
import ForumIndexPage from '../Forum/ForumIndexPage';
import ForumLayout from '../Forum/ForumLayout';
import ForumPostPage from '../Forum/ForumPostPage';
import Partners from '../Partners';
import PaymentStatus from '../PaymentStatus';
import Pro from '../Pro';
import SettingsPage from '../Settings/SettingsPage';
import Account from '../Settings/SettingsPage/Account';
import Affiliate from '../Settings/SettingsPage/Affiliate';
import EmailPreferences from '../Settings/SettingsPage/EmailPreferences';
import Gift from '../Settings/SettingsPage/Gift';
import Privacy from '../Settings/SettingsPage/Privacy';
import Team from '../Settings/SettingsPage/Team';
import EditTopicsSettings from '../Settings/UserProfile/EditTopicsSettings';
import Subscriber from '../Subscriber';
import SubscriberDirectory from '../SubscriberDirectory';
import UpdatePayment from '../UpdatePayment';
import EmailConfirmation from '../shared/EmailConfirmation';
import MetaTagLayout from './MetaTagLayout';
import ProtectedRoute from './ProtectedRoute';

const queryClient = new QueryClient();
const stripePromise = loadStripe(STRIPE_PUBLISH_KEY);

function AppRoutes() {
  useScrollToTop();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { currentUser } = useCurrentUser();

  // Set the rc param on every location change
  useEffect(() => {
    if (currentUser?.affiliate_code && !params.get('rc')) {
      params.set('rc', currentUser.affiliate_code);
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    }
  }, [params, currentUser?.affiliate_code, navigate]);

  return (
    <Routes>
      <Route element={<MetaTagLayout />}>
        <Route element={<ForumLayout />}>
          <Route path="/forum" element={<ForumIndexPage />} />
          <Route path="/forum/recent" element={<ForumIndexPage order="most_recent" />} />
          <Route path="/forum/comments" element={<ForumIndexPage order="most_comments" />} />
          <Route path="/forum/posts/:id" element={<ForumPostPage />} />
          <Route path="/forum/posts/:id/edit" element={<EditForumPostPage />} />
          <Route path="/forum/posts/new" element={<EditForumPostPage />} />
        </Route>
        <Route path="/partners" element={<Partners />} />
        <Route path="/pro" element={<Pro />} />
        <Route path="/insights" element={<CommunityInsightsPage />} />
        <Route path="/directory" element={<SubscriberDirectory />} />
        <Route path="/u/:username" element={<Subscriber />} />
        <Route path="/u/:username/:article_page_id" element={<Subscriber />} />
        <Route element={<ProtectedRoute />}>
          <Route path="/payment-status" element={<PaymentStatus />} />
          <Route path="/subscription/update-payment" element={<UpdatePayment />} />
          <Route element={<SettingsPage />}>
            <Route path="/unsubscribe" element={<EmailPreferences />} />
          </Route>
          <Route path="/settings" element={<SettingsPage />}>
            <Route index element={<Account />} />
            <Route path="email-preferences" element={<EmailPreferences />} />
            <Route path="affiliate-settings" element={<Affiliate />} />
            <Route path="gift-subscription" element={<Gift />} />
            <Route path="team-settings" element={<Team />} />
            <Route path="privacy-settings" element={<Privacy />} />
            <Route path="topics" element={<EditTopicsSettings />} />
          </Route>
        </Route>
        <Route
          path="/email-confirmation"
          element={<EmailConfirmation message={EMAIL_MESSAGE_CONFIRMATION} />}
        />
      </Route>
    </Routes>
  );
}

const App = ({
  env,
  currentUser,
  isWebview,
}: {
  env: Env;
  currentUser: User;
  isWebview: boolean;
}) => {
  return (
    <AppContextProvider env={env} initialCurrentUser={currentUser} isWebview={isWebview}>
      <QueryClientProvider client={queryClient}>
        <Elements stripe={stripePromise}>
          <div className="App">
            <BrowserRouter>
              <AppRoutes />
              <CustomToaster />
            </BrowserRouter>
          </div>
        </Elements>
      </QueryClientProvider>
    </AppContextProvider>
  );
};

export default App;
